import { AuthServiceInterface } from '@freelancer/auth';
import {
  androidSkill,
  CurrencyCode,
  employerUser,
  encodedUrlForUserProfile,
  freelancerUser,
  GenerateProfileViewUserOptions,
  LocationPreset,
  projectViewUserLocations,
  userFromUsersSelf,
  userInfoFromUser,
  verifiedUser,
} from '@freelancer/datastore/collections';
import { DatastoreInterface } from '@freelancer/datastore/core';
import { oneLine } from 'common-tags';
import {
  createBanners,
  createCertification,
  createEncodedUrl,
  createFreelancerReputation,
  createFreelancerReputationsRehireRate,
  createNotificationsPreferences,
  createOnlineOffline,
  createProfileViewUser,
  createProjectViewUser,
  createReferralInvitationCheck,
  createSimilarFreelancers,
  createSimilarShowcases,
  createTopSkills,
  createUser,
  createUserBalances,
  createUserCalifornianStatuses,
  createUserGiveGetDetail,
  createUserInfo,
  createUsersSelf,
} from '../document-creators';

/**
 * Used for App Store and Play Store screenshots.
 *
 * Only change this when you want to change those screenshots.
 */
export async function constructAppStoreProfileState(
  auth: AuthServiceInterface,
  datastore: DatastoreInterface,
  authUid: number,
) {
  // The employer viewing the site
  const userSelf = await createUsersSelf({
    userId: authUid,
    username: 'johnS',
    displayName: 'John S.',
  });

  const { id: employerId, username } = userSelf;

  const employer = await createUser({
    ...userFromUsersSelf(userSelf),
    ...employerUser(),
  });

  // The freelancer they are working with

  const freelancer = await createUser({
    ...freelancerUser(),
    username: 'alexanderL',
    displayName: 'Alexander L.',
  });

  await createProfileViewUser({
    user: freelancer,
    ...experiencedSydneyAndroidDeveloper(),
  });
  await createProjectViewUser({ user: freelancer });
  await createEncodedUrl({
    userId: employerId,
    ...encodedUrlForUserProfile(freelancer),
  });

  await createTopSkills({ userId: freelancer.id, skills: [androidSkill()] });
  await createCertification({ userId: freelancer.id });
  await createFreelancerReputation({ freelancerId: freelancer.id });

  await createOnlineOffline({ userIds: [freelancer.id] });
  await createFreelancerReputationsRehireRate({
    freelancerId: freelancer.id,
    reputationOptions: { total: 100, completePercentage: 0.9 },
  });
  // await createCanViewEmployerProfile(canViewEmployerProfileFromUser(employer));
  await createSimilarFreelancers({ userId: freelancer.id });
  await createSimilarShowcases({ userId: freelancer.id });

  // Other junk
  await createUserInfo(userInfoFromUser(employer));
  await createUserGiveGetDetail({ userId: employerId, username });
  await createUserCalifornianStatuses({ userIds: [employerId] });
  await createUserBalances();
  await createBanners();
  await createReferralInvitationCheck({ userId: employerId });
  await createNotificationsPreferences({});
}

/**
 * Used by the App Store screenshots.
 *
 * Only change if you want to change the screenshots.
 */
export function experiencedSydneyAndroidDeveloper(): Pick<
  Required<GenerateProfileViewUserOptions>,
  | 'currencyCode'
  | 'skills'
  | 'location'
  | 'preferredFreelancer'
  | 'registrationDate'
  | 'status'
  | 'profileDescription'
  | 'tagLine'
  | 'hourlyRate'
> {
  return {
    skills: [androidSkill()],
    currencyCode: CurrencyCode.AUD,
    location: projectViewUserLocations[LocationPreset.SYDNEY],
    preferredFreelancer: true,
    registrationDate: new Date().setFullYear(new Date().getFullYear() - 6),
    status: verifiedUser().userStatus,
    profileDescription: oneLine`
      Hello! I'm Alexander, a Sydney-based Android software engineer.
      I specialise in clean communication with a focus on simplicity.
    `,
    tagLine: `Android connoisseur`,
    hourlyRate: 40,
  };
}
